import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Plus } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

export function VisiteFormSheet({
  open, onOpenChange, editing, chantiers,
  formData, setFormData, submitting, onSubmit,
}) {
  const [newParticipant, setNewParticipant] = useState('');

  const addParticipant = () => {
    if (newParticipant.trim() && !formData.participants.includes(newParticipant.trim())) {
      setFormData({ ...formData, participants: [...formData.participants, newParticipant.trim()] });
      setNewParticipant('');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" aria-describedby="visite-form-desc">
        <SheetHeader>
          <SheetTitle>{editing ? 'Modifiér la visite' : 'Nouvelle visite'}</SheetTitle>
          <p id="visite-form-desc" className="text-sm text-[#4B5563]">
            {editing ? 'Modifiéz les informations' : 'Planifiez une nouvelle visite CSPS'}
          </p>
        </SheetHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-6">
          <div>
            <Label>Chantier *</Label>
            <Select value={formData.chantier_id}
              onValueChange={(v) => setFormData({ ...formData, chantier_id: v })}>
              <SelectTrigger className="mt-1 h-14" data-testid="visite-chantier-select">
                <SelectValue placeholder="Sélectionner un chantier" />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                {chantiers.map(c => (
                  <SelectItem key={c.id} value={c.id} className="py-3">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date de visite *</Label>
              <Input type="date" value={formData.date_visite}
                onChange={(e) => setFormData({ ...formData, date_visite: e.target.value })}
                required data-testid="visite-date" className="mt-1 h-14" />
            </div>
            <div>
              <Label>Type de visite</Label>
              <Select value={formData.type_visite}
                onValueChange={(v) => setFormData({ ...formData, type_visite: v })}>
                <SelectTrigger className="mt-1 h-14" data-testid="visite-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[100]">
                  <SelectItem value="inspection" className="py-3">Inspection</SelectItem>
                  <SelectItem value="chantier" className="py-3">Visite chantier</SelectItem>
                  <SelectItem value="coordination" className="py-3">Coordination</SelectItem>
                  <SelectItem value="accueil" className="py-3">Accueil</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Objet de la visite *</Label>
            <Input value={formData.objet}
              onChange={(e) => setFormData({ ...formData, objet: e.target.value })}
              placeholder="Ex: Inspection installations electriques" required
              data-testid="visite-objet" className="mt-1 h-14" />
          </div>

          <div>
            <Label>Statut</Label>
            <Select value={formData.status}
              onValueChange={(v) => setFormData({ ...formData, status: v })}>
              <SelectTrigger className="mt-1 h-14" data-testid="visite-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                <SelectItem value="planifiee" className="py-3">Planifiee</SelectItem>
                <SelectItem value="realisee" className="py-3">Realisee</SelectItem>
                <SelectItem value="annulee" className="py-3">Annulee</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Participants */}
          <div>
            <Label>Participants</Label>
            <div className="flex gap-2 mt-1">
              <Input value={newParticipant}
                onChange={(e) => setNewParticipant(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addParticipant(); } }}
                placeholder="Nom du participant" className="h-14 flex-1" />
              <Button type="button" onClick={addParticipant} className="h-14 bg-[#FF6B00] hover:bg-orange-700">
                <Plus size={20} />
              </Button>
            </div>
            {formData.participants.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.participants.map((p, i) => (
                  <span key={`participant-${p}-${i}`} className="inline-flex items-center gap-1 bg-orange-50 border border-orange-200 px-2.5 py-1 rounded-md text-sm text-orange-800">
                    {p}
                    <button type="button" onClick={() => setFormData({ ...formData, participants: formData.participants.filter((_, j) => j !== i) })}>
                      <span className="text-red-500 hover:text-red-700 text-lg leading-none">&times;</span>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label>Observations</Label>
            <Textarea value={formData.observations_text}
              onChange={(e) => setFormData({ ...formData, observations_text: e.target.value })}
              placeholder="Observations relevees lors de la visite..."
              rows={3} className="mt-1" data-testid="visite-observations" />
          </div>

          <div>
            <Label>Actions requises</Label>
            <Textarea value={formData.actions_requises}
              onChange={(e) => setFormData({ ...formData, actions_requises: e.target.value })}
              placeholder="Actions correctives a mettre en place..."
              rows={3} className="mt-1" data-testid="visite-actions" />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 h-14">Annuler</Button>
            <Button type="submit" disabled={submitting}
              className="flex-1 h-14 bg-[#FF6B00] hover:bg-orange-700" data-testid="visite-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Enregistrér' : 'Créer')}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
